import { useDeleteImageHardDelete, useFetchAdminDeletedImageList, usePatchRestoreDeletedImage } from '@api';
import {
  CategorySelectEnum,
  FromPageQueryParamValueEnum,
  AdminModalType,
  PathnameEnum,
  PathParamKeyEnum,
  QueryParamKeyEnum,
  ToastMessageEnum
} from '@constant';
import { useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useToast } from './useToast';

export const useAdminDeletedImageList = () => {
  const [modalType, setModalType] = useState<AdminModalType>(AdminModalType.IMAGE_UPLOAD_CANCEL);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [targetInfo, setTargetInfo] = useState<{
    imageId: number;
    theme: keyof typeof CategorySelectEnum;
    originFileName: string;
    sequenceId?: number;
  } | null>(null);

  const { showToast } = useToast();

  const tableHeader = [
    { id: 'id', label: 'ID', width: 250.67 },
    { id: 'theme', label: '테마', width: 250.67 },
    { id: 'genre', label: '장르', width: 250.67 },
    { id: 'fileName', label: '파일명', width: 728 },
    { id: 'restoreButton', label: '', width: 60 },
    { id: 'deleteButton', label: '', width: 60 }
  ];

  const navigate = useNavigate();
  const location = useLocation();
  const locationPathname = location.pathname;

  const [searchParams] = useSearchParams();
  const page = searchParams.get(QueryParamKeyEnum.PAGE) ? Number(searchParams.get(QueryParamKeyEnum.PAGE)) : 1;

  const { response, refetch } = useFetchAdminDeletedImageList(page);

  const { mutateAsync: restoreMutateAsync, isPending: isRestorePending } = usePatchRestoreDeletedImage();

  const { mutateAsync: hardDeleteMutateAsync, isPending: isHardDeletePending } = useDeleteImageHardDelete();

  const handlePageChange = (value: number) => {
    const queryParams = new URLSearchParams(location.search);
    queryParams.set(QueryParamKeyEnum.PAGE, String(value));
    const queryString = queryParams.toString();
    navigate(`${locationPathname}?${queryString}`, { replace: true });
  };

  const handleTargetClick = (
    imageId: number,
    theme: keyof typeof CategorySelectEnum,
    originFileName: string,
    sequenceId?: number
  ) => {
    setTargetInfo({ imageId: imageId, theme: theme, originFileName: originFileName, sequenceId: sequenceId });
  };

  const handleDeletedImageModalOpen = (
    imageId: number,
    theme: keyof typeof CategorySelectEnum,
    originFileName: string,
    sequenceId?: number
  ) => {
    setTargetInfo({ imageId: imageId, theme: theme, originFileName: originFileName, sequenceId: sequenceId });

    const currentSearch = location.search;
    const queryParams = new URLSearchParams(currentSearch);

    queryParams.set(QueryParamKeyEnum.IS_IMAGE_MODAL_VISIBLE, 'true');

    // 최초 클릭된 이미지의 아이디
    queryParams.set(QueryParamKeyEnum.INITIAL_IMAGE_ID, imageId.toString());

    // 이미지 아이디 및 시퀀스 아이디
    queryParams.set(QueryParamKeyEnum.IMAGE_ID, imageId.toString());
    if (sequenceId) {
      queryParams.set(QueryParamKeyEnum.SEQUENCE_ID, sequenceId.toString());
    }

    navigate(`${locationPathname}?${queryParams.toString()}`);
  };

  const handleDeletedImageModalClose = () => {
    const currentSearch = location.search;
    const queryParams = new URLSearchParams(currentSearch);

    queryParams.delete(QueryParamKeyEnum.IS_IMAGE_MODAL_VISIBLE);
    queryParams.delete(QueryParamKeyEnum.IMAGE_ID);
    queryParams.delete(QueryParamKeyEnum.SEQUENCE_ID);

    navigate(`${locationPathname}?${queryParams.toString()}`);
  };

  const handleRestoreButtonClick = () => {
    setModalType(AdminModalType.RESTORE_CONFIRM);
    setIsModalVisible(true);
  };

  const handleHardDeleteButtonClick = () => {
    setModalType(AdminModalType.HARD_DELETE_CONFIRM);
    setIsModalVisible(true);
  };

  const handleModalConfirmClick = async () => {
    const queryParams = new URLSearchParams(); // 페이지 이동시 이동 전 페이지의 query string이 포함되지 않도록 빈 객체로 새로 생성

    if (modalType === AdminModalType.RESTORE_CONFIRM && targetInfo?.imageId) {
      await restoreMutateAsync(targetInfo?.imageId)
        .then(() => {
          refetch();
          setModalType(
            targetInfo.theme === 'STANDARD'
              ? AdminModalType.RESTORE_STANDARD_IMAGE_SUCCESS
              : AdminModalType.RESTORE_ADULTS_ONLY_IMAGE_SUCCESS
          );
        })
        .catch(() => {
          setModalType(AdminModalType.RESTORE_FAIL);
        });
    } else if (modalType === AdminModalType.RESTORE_STANDARD_IMAGE_SUCCESS) {
      if (targetInfo?.sequenceId) {
        queryParams.set(QueryParamKeyEnum.SEQUENCE_ID, String(targetInfo.sequenceId));
      }

      queryParams.set(QueryParamKeyEnum.FROM_PAGE, FromPageQueryParamValueEnum.DELETED_IMAGE_LIST);
      queryParams.set(QueryParamKeyEnum.THEME, CategorySelectEnum.STANDARD);

      const queryString = queryParams.toString();

      navigate(
        `${PathnameEnum.ADMIN_IMAGE_EDIT_PAGE.replace(PathParamKeyEnum.IMAGE_ID, String(targetInfo?.imageId))}?${queryString}`,
        { replace: true }
      );
    } else if (modalType === AdminModalType.RESTORE_ADULTS_ONLY_IMAGE_SUCCESS) {
      if (targetInfo?.sequenceId) {
        queryParams.set(QueryParamKeyEnum.SEQUENCE_ID, String(targetInfo.sequenceId));
      }

      queryParams.set(QueryParamKeyEnum.FROM_PAGE, FromPageQueryParamValueEnum.DELETED_IMAGE_LIST);
      queryParams.set(QueryParamKeyEnum.THEME, CategorySelectEnum.ADULTS_ONLY);

      const queryString = queryParams.toString();

      navigate(
        `${PathnameEnum.ADMIN_IMAGE_EDIT_PAGE.replace(PathParamKeyEnum.IMAGE_ID, String(targetInfo?.imageId))}?${queryString}`,
        { replace: true }
      );
    } else if (modalType === AdminModalType.HARD_DELETE_CONFIRM && targetInfo?.imageId) {
      await hardDeleteMutateAsync(targetInfo?.imageId)
        .then(() => {
          refetch();
          setIsModalVisible(false);
          handleDeletedImageModalClose();
          showToast(ToastMessageEnum.IMAGE_HARD_DELETE_SUCCESS);
        })
        .catch(() => {
          setModalType(AdminModalType.HARD_DELETE_FAIL);
        });
    }
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
  };

  return {
    tableHeader,
    response,
    page,
    handlePageChange,

    handleTargetClick,
    handleDeletedImageModalOpen,
    handleDeletedImageModalClose,

    targetInfo,
    modalType,
    isModalVisible,
    handleRestoreButtonClick,
    handleHardDeleteButtonClick,
    handleModalConfirmClick,
    handleModalClose,

    isRestorePending,
    isHardDeletePending
  };
};
